<script setup>
import { computed, shallowRef } from 'vue';
import UiCard from '@/components/ui/UiCard.vue';
import UiBadge from '@/components/ui/UiBadge.vue';
import PageTabs from '@/components/Utils/PageTabs.vue';
import StudentCell from './StudentCell.vue';
import { matchesSearch } from '@/utils/search';
import { formatDate, JHS_GRADES, lardoSeverity, SHS_GRADES } from '@/constants/risk';

// Students who need attention: "At risk" (low grades) and "LARDO" alerts,
// with search and a grade filter. Rows open their details.
const props = defineProps({
  atRisk: { type: Array, required: true },
  lardo: { type: Array, required: true },
  grades: { type: Array, required: true }, // grade numbers this admin sees
  loading: { type: Boolean, default: false },
});
const emit = defineEmits(['view-student', 'view-alert']);

const tab = shallowRef('risk');
const search = shallowRef('');
const gradeFilter = shallowRef(null);

const tabs = computed(() => [
  { id: 'risk', name: `At risk · ${props.atRisk.length}` },
  { id: 'lardo', name: `LARDO · ${props.lardo.length}` },
]);

const gradeOptions = computed(() => {
  const options = [{ title: 'All grades', value: null }];
  const hasJhs = props.grades.some((g) => JHS_GRADES.includes(g));
  const hasShs = props.grades.some((g) => SHS_GRADES.includes(g));
  if (hasJhs && hasShs) {
    options.push(
      { title: 'Junior High (7–10)', value: 'JHS' },
      { title: 'Senior High (11–12)', value: 'SHS' },
    );
  }
  return [...options, ...props.grades.map((g) => ({ title: `Grade ${g}`, value: g }))];
});
const matchesGrade = (grade) => {
  const f = gradeFilter.value;
  if (f === null) return true;
  if (f === 'JHS') return JHS_GRADES.includes(grade);
  if (f === 'SHS') return SHS_GRADES.includes(grade);
  return grade === f;
};

const riskRows = computed(() => props.atRisk.filter((s) => matchesGrade(s.grade)));
const lardoRows = computed(() =>
  props.lardo
    .filter((a) => matchesGrade(a.grade))
    .map((a) => ({ ...a, severity: lardoSeverity(a.remarks) })),
);

const riskHeaders = [
  { title: 'Student', key: 'name' },
  { title: 'Lowest grade', key: 'lowest', width: 130 },
  { title: 'Subjects', key: 'subjectText', sortable: false },
  { title: 'Grade & section', key: 'gradeLevel' },
  { title: 'Class adviser', key: 'adviser' },
  { title: '', key: 'open', sortable: false, align: 'end', width: 56 },
];
const lardoHeaders = [
  { title: 'Student', key: 'name' },
  { title: 'Alert', key: 'remarks', width: 150 },
  { title: 'Status', key: 'pending', width: 150 },
  { title: 'Grade & section', key: 'grade_level' },
  { title: 'Class adviser', key: 'adviser' },
  { title: 'Flagged', key: 'created_at', width: 130 },
  { title: '', key: 'open', sortable: false, align: 'end', width: 56 },
];

const rowProps = () => ({ class: 'watch__row' });
// The LRN is shown under the name, not as a column, so search it too.
const searchWithLrn = (value, query, item) => matchesSearch(query, [value, item?.raw?.lrn]);
</script>

<template>
  <UiCard
    title="Students who need attention"
    description="Low grades and LARDO alerts for the selected school year. Select a row for details."
    flush
  >
    <template #toolbar>
      <PageTabs v-model="tab" :items="tabs" />
      <span v-if="tab === 'risk'" class="watch__legend">
        <UiBadge variant="red">≤ 75 At risk</UiBadge>
        <UiBadge variant="amber">76–80 Watch</UiBadge>
      </span>
      <div class="ui-card__toolbar-end">
        <v-select
          v-model="gradeFilter"
          :items="gradeOptions"
          density="compact"
          hide-details
          aria-label="Grade filter"
          class="watch__filter"
        />
        <v-text-field
          v-model="search"
          placeholder="Search name, LRN, adviser"
          prepend-inner-icon="mdi-magnify"
          density="compact"
          hide-details
          clearable
          class="card-search"
        />
      </div>
    </template>

    <v-data-table
      v-if="tab === 'risk'"
      :headers="riskHeaders"
      :items="riskRows"
      :search="search"
      :custom-filter="searchWithLrn"
      :loading="loading"
      :items-per-page="10"
      :row-props="rowProps"
      class="table-flush"
      @click:row="(_, { item }) => emit('view-student', item)"
    >
      <template #[`item.name`]="{ item }">
        <StudentCell :name="item.name" :lrn="item.lrn" />
      </template>
      <template #[`item.lowest`]="{ item }">
        <UiBadge :variant="item.risk.variant">{{ item.lowest ?? '—' }} · {{ item.risk.label }}</UiBadge>
      </template>
      <template #[`item.subjectText`]="{ item }">
        <span class="watch__subjects">
          <strong>{{ item.subjects.length }}</strong>
          <span class="watch__muted">{{ item.subjectText }}</span>
        </span>
      </template>
      <template #[`item.gradeLevel`]="{ item }">
        {{ [item.gradeLevel, item.section].filter(Boolean).join(' · ') || '—' }}
      </template>
      <template #[`item.open`]="{ item }">
        <v-btn
          icon="mdi-chevron-right"
          variant="text"
          size="small"
          :aria-label="`View ${item.name}`"
          @click.stop="emit('view-student', item)"
        />
      </template>
      <template #no-data>
        <div class="table-empty watch__empty">
          <v-icon size="28">mdi-check-circle-outline</v-icon>
          <strong>No students at risk</strong>
          <span>Everyone matching these filters is above 80.</span>
        </div>
      </template>
    </v-data-table>

    <v-data-table
      v-else
      :headers="lardoHeaders"
      :items="lardoRows"
      :search="search"
      :custom-filter="searchWithLrn"
      :loading="loading"
      :items-per-page="10"
      :row-props="rowProps"
      class="table-flush"
      @click:row="(_, { item }) => emit('view-alert', item)"
    >
      <template #[`item.name`]="{ item }">
        <StudentCell :name="item.name" :lrn="item.lrn" />
      </template>
      <template #[`item.remarks`]="{ item }">
        <UiBadge :variant="item.severity.variant">
          <v-icon size="14">{{ item.severity.icon }}</v-icon>
          {{ item.severity.label }}
        </UiBadge>
      </template>
      <template #[`item.pending`]="{ item }">
        <UiBadge :variant="item.pending ? 'outline' : 'green'">
          {{ item.pending ? 'Pending action' : 'Action recorded' }}
        </UiBadge>
      </template>
      <template #[`item.grade_level`]="{ item }">
        {{ [item.grade_level, item.room_name].filter(Boolean).join(' · ') || '—' }}
      </template>
      <template #[`item.created_at`]="{ item }">
        <span class="watch__muted">{{ formatDate(item.created_at) }}</span>
      </template>
      <template #[`item.open`]="{ item }">
        <v-btn
          icon="mdi-chevron-right"
          variant="text"
          size="small"
          :aria-label="`View alert for ${item.name}`"
          @click.stop="emit('view-alert', item)"
        />
      </template>
      <template #no-data>
        <div class="table-empty watch__empty">
          <v-icon size="28">mdi-check-circle-outline</v-icon>
          <strong>No LARDO alerts</strong>
          <span>New alerts appear here as attendance is recorded.</span>
        </div>
      </template>
    </v-data-table>
  </UiCard>
</template>

<style scoped>
.watch__legend {
  display: inline-flex;
  gap: 6px;
}
.watch__filter {
  width: 190px;
  flex: 0 0 auto;
}
.watch__subjects {
  display: inline-flex;
  gap: 6px;
  max-width: 280px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.watch__muted {
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--muted-foreground);
}
.watch__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  font-size: 14px;
}
.watch__empty strong {
  color: var(--foreground);
  font-weight: 500;
}
:deep(.watch__row) {
  cursor: pointer;
}
</style>
